import './applyConfirmVehiclePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { get, isEmpty, toString } from 'lodash';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import VehicleImagePlaceholder from '../../../assets/vehicle-placeholder.png';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { resetStore as defaultResetStore } from '../../../store/_core/resetStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';
import { ApplyConfirmVehiclePageProps } from './types/ApplyConfirmVehiclePageProps';

export function Component({
  resetStore = defaultResetStore,
}: ApplyConfirmVehiclePageProps): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { setDrawerOpen } = useNavigationPaneStore();

  const { isLoading } = useApiStore();

  const {
    applicationType,
    computed: { yearMakeModel },
    fetchVehicleImageByTrim,
    fetchVehicleImageByTrimFailed,
    fetchVehicleTrims,
    fetchVehicleTrimsFailedMessage,
    isDuplicateVehicleError,
    mileage,
    payoffAmount,
    purchasePrice,
    resetSubmitValues,
    setMileage,
    setPayoffAmount,
    setPurchasePrice,
    setTrimByValue,
    submit,
    submitCompleted,
    submitFailedMessage,
    trim,
    trims,
    vehicleImage,
  } = useApplyStore();

  const { t } = useTranslation();
  const { previous } = useWorkflowStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const trimValidation = {
    required: t('validation.trim.required'),
  };

  const mileageValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.number.parser(value),
    required: t('validation.mileage.required'),
  };

  const payoffAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.payoffAmount.required'),
    min: {
      value: 1,
      message: t('validation.payoffAmount.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.payoffAmount.max', { max: '$1,000,000' }),
    },
  };

  const purchasePriceValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.purchasePrice.required'),
    min: {
      value: 1,
      message: t('validation.purchasePrice.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.purchasePrice.max', { max: '$1,000,000' }),
    },
  };

  const [imageLoading, setImageLoading] = useState(false);

  const displayVehicleImage = useMemo(() => {
    return vehicleImage && !imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPlaceholderImage = useMemo(() => {
    return isEmpty(vehicleImage) || imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPayoffAmount = useMemo(() => {
    return applicationType === ApplicationType.REFINANCE;
  }, [applicationType]);

  const displayPurchasePrice = useMemo(() => {
    return (
      applicationType === ApplicationType.PURCHASE ||
      applicationType === ApplicationType.LEASE_BUYOUT
    );
  }, [applicationType]);

  const nextStep = (): void => {
    const {
      computed: { vehicleId },
    } = useDashboardStore.getState();

    // Reset applicant and coapplicant stores to ensure a clean state for the applicant workflow.
    resetStore(useApplicantStore);
    resetStore(useCoapplicantStore);

    navigateWithCallback(`/applicant/${vehicleId}`).then(() => {
      // Reset the apply store to ensure a clean state for the next application.
      resetStore(useApplyStore);
    });
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const reloadVehicleImage = (event: ChangeEvent<HTMLSelectElement>): void => {
    const selectedTrim = trims.find(
      (trimObj) => trimObj.value === event.target.value,
    );

    if (selectedTrim) {
      resetSubmitValues();

      setImageLoading(true);
      fetchVehicleImageByTrim(selectedTrim);
    }
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { trimValue, mileageValue, payoffAmountValue, purchasePriceValue } =
      values;

    if (
      !trimValue ||
      !mileageValue ||
      (displayPayoffAmount && !payoffAmountValue) ||
      (displayPurchasePrice && !purchasePriceValue)
    ) {
      return;
    }

    setTrimByValue(trimValue);
    setMileage(mileageValue);

    if (displayPayoffAmount) {
      setPayoffAmount(payoffAmountValue);
    }

    if (displayPurchasePrice) {
      setPurchasePrice(purchasePriceValue);
    }

    submit();
  };

  useEffect(() => {
    resetSubmitValues();

    fetchVehicleTrims();

    if (!trim) {
      // when trim is not available, reset the vehicle image
      useApplyStore.setState({ vehicleImage: null });

      return;
    }

    setImageLoading(true);

    fetchVehicleImageByTrim(trim);
  }, []);

  useEffect(() => {
    // image loading is tracked by the onLoad event of the image tag.
    // This is done to prevent the loading animation from ending upon the completion of the request alone,
    // whereas the animation should end when the image is fully loaded.

    // if the request for the image url failed, end the loading animation
    if (fetchVehicleImageByTrimFailed) {
      setImageLoading(false);
    }
  }, [fetchVehicleImageByTrimFailed]);

  useEffect(() => {
    if (submitCompleted && !submitFailedMessage) {
      nextStep();
    }
  }, [submitCompleted, submitFailedMessage]);

  return (
    <div className="apply-confirm-vehicle-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyConfirmVehicle.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyConfirmVehicle.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.applyConfirmVehicle.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyConfirmVehicle.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main data-testid={locator.pages.applyConfirmVehicle.main.root}>
            <figure>
              <div>
                <img
                  alt={t('imageAlts.selectedVehicle')}
                  className={displayVehicleImage ? 'show' : 'hide'}
                  data-testid={
                    locator.pages.applyConfirmVehicle.main.selectedVehicleImage
                  }
                  src={vehicleImage || ''}
                  onLoad={() => {
                    setImageLoading(false);
                  }}
                />

                <img
                  alt={t('imageAlts.selectedVehicle')}
                  className={displayPlaceholderImage ? 'show' : 'hide'}
                  data-testid={
                    locator.pages.applyConfirmVehicle.main
                      .placeholderVehicleImage
                  }
                  src={VehicleImagePlaceholder}
                />

                {imageLoading && <span className="skeleton-loading" />}
              </div>
            </figure>
            <form
              data-testid={locator.pages.applyConfirmVehicle.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyConfirmVehicle.form.h1}>
                  {yearMakeModel}
                </h1>
                <h2 data-testid={locator.pages.applyConfirmVehicle.form.h2}>
                  {t('ApplyConfirmVehiclePage.subtitle')}
                </h2>

                {!isEmpty(fetchVehicleTrimsFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.applyConfirmVehicle.alert
                          .vehicleTrimsError
                      }
                      icon={false}
                      severity="error"
                    >
                      {fetchVehicleTrimsFailedMessage}
                    </Alert>
                  </div>
                )}

                {isNonEmptyString(submitFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.applyConfirmVehicle.alert
                          .submitFailedError
                      }
                      icon={false}
                      severity="error"
                    >
                      <Trans
                        components={{
                          dashboard: <Link to="/dashboard/garage" />,
                        }}
                      >
                        {submitFailedMessage}
                      </Trans>
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div>
                    <TextField
                      {...register('trimValue', {
                        ...trimValidation,
                        onChange: reloadVehicleImage,
                      })}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      error={!isEmpty(errors.trimValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applyConfirmVehicle.form.trimSelect,
                      }}
                      label={t('ApplyConfirmVehiclePage.trim')}
                      value={watch('trimValue', get(trim, 'value') || '')}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('ApplyConfirmVehiclePage.trim')}
                      </MenuItem>

                      {trims.map((trimObj) => {
                        return (
                          <MenuItem key={trimObj.value} value={trimObj.value}>
                            {trimObj.label}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.trimValue) && (
                      <span
                        data-testid={
                          locator.pages.applyConfirmVehicle.form.trimSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.trimValue.message)}
                      </span>
                    )}
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('mileageValue', mileageValidation)}
                      defaultValue={inputEventHandler.number.formatter(mileage)}
                      error={!isEmpty(errors.mileageValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applyConfirmVehicle.form.mileageInput,
                        inputMode: 'numeric',
                        min: 0,
                      }}
                      label={t('ApplyConfirmVehiclePage.mileage')}
                      type="text"
                      variant="outlined"
                      fullWidth
                      onChange={inputEventHandler.number.change(
                        'mileageValue',
                        setValue,
                      )}
                      onKeyDown={inputEventHandler.number.keydown}
                      onPasteCapture={inputEventHandler.number.pasteCapture}
                    />

                    {!isEmpty(errors.mileageValue) && (
                      <span
                        data-testid={
                          locator.pages.applyConfirmVehicle.form
                            .mileageInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.mileageValue.message)}
                      </span>
                    )}
                  </div>

                  {displayPayoffAmount && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'payoffAmountValue',
                          payoffAmountValidation,
                        )}
                        defaultValue={inputEventHandler.currency.formatter(
                          payoffAmount,
                        )}
                        error={!isEmpty(errors.payoffAmountValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applyConfirmVehicle.form
                              .payoffAmountInput,
                          inputMode: 'numeric',
                        }}
                        label={t('ApplyConfirmVehiclePage.payoffAmount')}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'payoffAmountValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'payoffAmountValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'payoffAmountValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.payoffAmountValue) && (
                        <span
                          data-testid={
                            locator.pages.applyConfirmVehicle.form
                              .payoffAmountInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.payoffAmountValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {displayPurchasePrice && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'purchasePriceValue',
                          purchasePriceValidation,
                        )}
                        defaultValue={inputEventHandler.currency.formatter(
                          purchasePrice,
                        )}
                        error={!isEmpty(errors.purchasePriceValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applyConfirmVehicle.form
                              .purchasePriceInput,
                          inputMode: 'numeric',
                        }}
                        label={t('ApplyConfirmVehiclePage.purchasePrice')}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'purchasePriceValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'purchasePriceValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'purchasePriceValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.purchasePriceValue) && (
                        <span
                          data-testid={
                            locator.pages.applyConfirmVehicle.form
                              .purchasePriceInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.purchasePriceValue.message)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applyConfirmVehicle.form.submitButton
                  }
                  disabled={
                    (isLoading && !imageLoading) ||
                    isDuplicateVehicleError ||
                    !isValid
                  }
                  loading={isLoading && !imageLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplyConfirmVehiclePage.continue')}
                </LoadingButton>
              </div>
              <footer
                data-testid={locator.pages.applyConfirmVehicle.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyConfirmVehiclePage';
